import { useState, useCallback } from 'react';
import { AppSettings, DEFAULT_CRITICALITY_LEVELS } from '../types';
import { getSettings, saveSettings } from '../services/storageService';
import { requireCompanyId } from './requireCompanyId';

const DEFAULT_SETTINGS: AppSettings = {
  currency: 'EUR',
  currencySymbol: '\u20ac',
  renewalWarningDays: 30,
  noticeWarningDays: 60,
  categories: ['Saas Manager'],
  tags: ['Governance', 'Training'],
  teams: [],
  allowedEmailDomains: [],
  criticalityLevels: DEFAULT_CRITICALITY_LEVELS,
  onboardingCompleted: false,
};

function sanitize(raw: AppSettings | null): AppSettings {
  if (!raw || typeof raw !== 'object') return DEFAULT_SETTINGS;
  return {
    currency: typeof raw.currency === 'string' ? raw.currency : 'EUR',
    currencySymbol: typeof raw.currencySymbol === 'string' ? raw.currencySymbol : '\u20ac',
    currencyRates: raw.currencyRates,
    renewalWarningDays: Number(raw.renewalWarningDays) || 30,
    noticeWarningDays: Number(raw.noticeWarningDays) || 60,
    categories: Array.isArray(raw.categories) ? raw.categories : [],
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    teams: Array.isArray(raw.teams) ? raw.teams : [],
    allowedEmailDomains: Array.isArray(raw.allowedEmailDomains)
      ? raw.allowedEmailDomains.filter((d): d is string => typeof d === 'string' && d.trim().length > 0)
      : [],
    criticalityLevels: Array.isArray(raw.criticalityLevels) && raw.criticalityLevels.length > 0
      ? raw.criticalityLevels
      : DEFAULT_CRITICALITY_LEVELS,
    onboardingCompleted: raw.onboardingCompleted === true,
    activationChecklist: raw.activationChecklist,
  };
}

export function useSettings(companyId: string | null) {
  const [settings, setSettings] = useState<AppSettings>(DEFAULT_SETTINGS);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runAction = useCallback(async <T>(action: (cid: string) => Promise<T>): Promise<T> => {
    setLoading(true);
    setError(null);
    try {
      const cid = requireCompanyId(companyId);
      return await action(cid);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unexpected error';
      setError(message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  const load = useCallback(async () => {
    try {
      const data = await runAction((cid) => getSettings(cid));
      const safe = sanitize(data);
      setSettings(safe);
      return safe;
    } catch {
      setSettings(DEFAULT_SETTINGS);
      return DEFAULT_SETTINGS;
    } finally {
      setSettingsLoaded(true);
    }
  }, [runAction]);

  const save = useCallback(async (newSettings: AppSettings) => {
    const updated = await runAction((cid) => saveSettings(newSettings, cid));
    setSettings(updated);
    return updated;
  }, [runAction]);

  return { settings, setSettings, loading, error, load, save, settingsLoaded };
}
